import React, { useState, useEffect } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { EmptyImage } from '@vlabs/shared/components/empty-image';
import { PhotoUploadForm } from '@vlabs/shared/components/photo-upload-form';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { detectOnImageRequest, DETECTION_TYPES } from '@vlabs/shared/requests/detect-on-image-request';
import { Control, Margin, Divider } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// FIXME: требуется рефакторинг, или вынести в другое место или выкинуть
// FIXME: обработать ошибку на верификацию при невыбранном лице (если несколько кандидатов - выберите сначала лицо)
import './VerifyForm.sass';

const searchByRawDescriptor = async ({
  faceId,
  personAttributes,
  selectedCandidates,
  candidates,
}) => {
  const { data: { matches } } = await apiContainer.lunaClient.matcher.raw({
    references: [
      {
        type: 'raw_descriptor',
        id: faceId,
        data: {
          descriptor: personAttributes.face_descriptor.descriptor,
          version: personAttributes.face_descriptor.descriptor_version,
        },
      },
    ],
    candidates: [
      {
        type: 'sdk_descriptor',
        id: selectedCandidates,
        data: candidates?.byId[selectedCandidates]?.attributes?.descriptor?.sdk_descriptor,
      },
    ],
  });
  return matches;
};

// FIXME: это не компонент формы, перепроверить и поменять название чтобы не путаться
export function VerifyForm({ photo, faceId }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(undefined);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [matchResultSimilarity, setMatchResultSimilarity] = useState();
  const [isEmpty, setIsEmpty] = useState(!photo);

  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (!file) return;
    async function run() {
      const imageDetections = await detectOnImageRequest(
        file,
        [DETECTION_TYPES.FACES],
        apiContainer.lunaClient.sdk.estimateFace,
        apiContainer.lunaClient.sdk.estimateBody,
      );
      setCandidates(imageDetections);
    }
    run();
  }, [file]);

  const onFileSelect = async (newFile) => {
    if (!newFile) return;

    setFile(newFile);
  };

  const onReset = () => {
    setFile(undefined);
    setCandidates(undefined);
    setSelectedCandidates([]);
    setIsVerified(false);
    setMatchResultSimilarity(undefined);
  };

  const onMatch = async () => {
    const {
      data: {
        attributes: personAttributes,
      },
    } = await apiContainer.lunaClient.faces.attributes(faceId).get(faceId);

    const matches = await searchByRawDescriptor({
      faceId, personAttributes, selectedCandidates, candidates,
    });
    setIsVerified(true);
    setMatchResultSimilarity(matches?.[0].matches?.[0].similarity);
  };

  const renderResultBlock = () => {
    if (!isVerified) {
      return (
        <PhotoUploadForm
          detections={candidates?.all}
          errorProps={{ errors: { message: (file && candidates?.length === 0) && t('selected_image_no_faces') } }}
          file={file}
          onFileSelect={onFileSelect}
          onResetFile={onReset}
          onSelectSampleId={([facesId]) => facesId && setSelectedCandidates(facesId)}
        />
      );
    }

    return (
      <React.Fragment>
        <img
          alt="фотография"
          className="VerifyFormFaceHolder"
          data-testid="verificatedImage"
          src={file.binary || null}
        />
        <SimilarityTag
          className="FaceCardTag"
          data-testid="similarity_tag"
          value={matchResultSimilarity}
        />
      </React.Fragment>
    );
  };

  return (
    <div className="VerifyForm">
      <h5>{t('externalID:modal.verification.title')}</h5>
      <Divider small />

      <div className="VerifyFormPhotoContainer">
        <div className="FacePhotoContainer">
          {!!photo && !isEmpty && (
            <img
              alt="фотография"
              className="VerifyFormFaceHolder"
              data-testid="faceImageForVerification"
              onError={() => setIsEmpty(true)}
              src={photo || null}
            />
          )}
          {!!isEmpty && <EmptyImage className="EmptyFaceMessage" />}
        </div>
        <Margin right />
        <div className="FacePhotoContainer">{renderResultBlock()}</div>
      </div>
      <Divider />

      <div className="VerifyFormBottom">
        <Control.Button
          data-testid="form.verifyButton"
          disabled={!selectedCandidates?.length}
          onClick={onMatch}
          type="button"
        >
          {t('externalID:action.verify')}
        </Control.Button>

        <Margin right />
        <Control.Button
          disabled={file === null || file === undefined}
          kind="negative"
          onClick={onReset}
        >
          {t('externalID:button.reset')}
        </Control.Button>
      </div>
    </div>
  );
}

VerifyForm.propTypes = {
  photo: PropTypes.string.isRequired,
  faceId: PropTypes.string.isRequired,
};
